interface RelationshipDecisionsGuardrailConfig {
  therapeuticFrameworks: {
    nonDirective: boolean;
    clientCentered: boolean;
    innerWisdomFocused: boolean;
    valuesAligned: boolean;
  };
  decisionGuidelines: {
    neutralStance: boolean;
    personalAgency: boolean;
    claritySupport: boolean;
    noOutcomeBias: boolean;
  };
  safetyProtocols: {
    abuseScreening: boolean;
    safetyPlanning: boolean;
    resourceProvision: boolean;
    crisisSupport: boolean;
  };
  emotionalSupport: {
    selfCompassion: boolean;
    ambiguityTolerance: boolean;
    processRespect: boolean;
    courageBuilding: boolean;
  };
}

export const RELATIONSHIP_DECISIONS_GUARDRAILS: RelationshipDecisionsGuardrailConfig = {
  therapeuticFrameworks: {
    nonDirective: true,
    clientCentered: true,
    innerWisdomFocused: true,
    valuesAligned: true
  },
  decisionGuidelines: {
    neutralStance: true,
    personalAgency: true,
    claritySupport: true,
    noOutcomeBias: false
  },
  safetyProtocols: {
    abuseScreening: true,
    safetyPlanning: true,
    resourceProvision: true,
    crisisSupport: true
  },
  emotionalSupport: {
    selfCompassion: true,
    ambiguityTolerance: true,
    processRespect: true,
    courageBuilding: true
  }
};

export const validateRelationshipDecisionsContent = (content: string): { isValid: boolean; violations: string[]; corrections: string[] } => {
  const violations: string[] = [];
  const corrections: string[] = [];

  // Check for directive/prescriptive language
  const directiveWords = ['should', 'must', 'need to', 'have to', 'ought', 'supposed to'];
  const lowerContent = content.toLowerCase();
  
  directiveWords.forEach(word => {
    if (lowerContent.includes(word)) {
      violations.push(`Directive language detected: "${word}"`);
      corrections.push('Use exploratory, client-centered language that honors personal choice');
    }
  });

  // Check for outcome bias
  const biasedWords = ['leave', 'stay', 'divorce', 'breakup', 'together'];
  const hasBias = biasedWords.some(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    return regex.test(content) && (content.toLowerCase().includes('should ' + word) || content.toLowerCase().includes('need to ' + word));
  });
  
  if (hasBias) {
    violations.push('Outcome bias detected in decision guidance');
    corrections.push('Maintain neutrality and focus on inner wisdom rather than specific outcomes');
  }

  // Ensure inner wisdom focus
  const wisdomWords = ['inner wisdom', 'intuition', 'heart', 'truth', 'values', 'authentic self'];
  const hasWisdom = wisdomWords.some(word => lowerContent.includes(word));
  
  if (!hasWisdom) {
    violations.push('Missing inner wisdom/values focus');
    corrections.push('Include language that connects to personal values and inner knowing');
  }

  // Check for safety considerations
  if (lowerContent.includes('difficult') || lowerContent.includes('hard') || lowerContent.includes('challenging')) {
    if (!lowerContent.includes('safe') && !lowerContent.includes('support') && !lowerContent.includes('resources')) {
      violations.push('Missing safety/support language for difficult decisions');
      corrections.push('Include safety planning and resource awareness for challenging situations');
    }
  }

  // Ensure self-compassion elements
  const compassionWords = ['gentle', 'kind', 'patient', 'compassion', 'understanding', 'forgive'];
  const hasCompassion = compassionWords.some(word => lowerContent.includes(word));
  
  if (!hasCompassion) {
    violations.push('Missing self-compassion elements');
    corrections.push('Include gentle, self-compassionate language for this difficult process');
  }

  return {
    isValid: violations.length === 0,
    violations,
    corrections
  };
};

export const createRelationshipDecisionsSafetyPrompt = (): string => {
  return `
CRITICAL RELATIONSHIP DECISIONS GUIDELINES:
- Maintain complete neutrality - do not suggest staying or leaving
- Focus on connecting client to their inner wisdom and values
- Use exploratory, non-directive language throughout
- Honor the client's timing and decision-making process
- Include self-compassion and patience for this difficult journey
- Screen for safety concerns without being directive
- Provide emotional support for ambiguity and uncertainty
- Respect that clarity may take time and multiple sessions
- Honor Michele's expertise in relationship dynamics
- Avoid any language that could be seen as advice-giving
- Include grounding practices for decision-making anxiety
- Acknowledge the courage it takes to face these questions
`;
};

export const isRelationshipDecisionsContentSafe = (content: string): boolean => {
  const validation = validateRelationshipDecisionsContent(content);
  return validation.isValid;
};